import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import TagIcon from "@redotech/redo-icons/line-chart-down-01_filled.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import ToolIcon from "@redotech/redo-icons/tool-02.svg";
import UserPlusIcon from "@redotech/redo-icons/user-plus-01.svg";
import {
  flowCategoriesWithSkipConditions,
  TriggerStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { getTriggerDisplayName } from "@redotech/redo-model/advanced-flow/triggers";
import {
  WorkOrderFlowTriggerConditions,
  WorkOrderTriggerSource,
} from "@redotech/redo-model/work-order-flow/work-order-flow";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { calculateSelectionWrapperStyles } from "@redotech/redo-web/selection-wrapper-styles";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Handle, Position } from "@xyflow/react";
import classNames from "classnames";
import { useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";

export interface TriggerNodeData {
  step: PartialWithRequiredType<TriggerStep>;
  triggerConditions?: WorkOrderFlowTriggerConditions;
  triggerSource?: WorkOrderTriggerSource;
  triggerBomIds?: string[];
  flowName?: string;
}

export function getTriggerIconForSchemaType(
  schemaType: SchemaType | undefined,
): (props: any) => React.ReactElement {
  switch (schemaType) {
    case SchemaType.CHECKOUT_TREE:
    case SchemaType.RCO_CHECKOUT_PACKAGE:
    case SchemaType.POST_PURCHASE_FLOW:
      return ShoppingCartIcon;
    case undefined:
    case SchemaType.EMAIL_MARKETING_SIGNUP:
    case SchemaType.MARKETING_BACK_IN_STOCK:
    case SchemaType.MARKETING_LOW_INVENTORY:
    case SchemaType.MARKETING_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_CAMPAIGN:
    case SchemaType.MARKETING_CHECKOUT_ABANDONMENT:
    case SchemaType.MARKETING_CART_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_CART_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT:
    case SchemaType.MARKETING_EMAIL:
    case SchemaType.MARKETING_SEGMENT_MEMBERSHIP_CHANGE:
    case SchemaType.MARKETING_WARRANTY_REGISTRATION:
    case SchemaType.PRODUCT_SUBSCRIPTION_UPCOMING_CHARGE:
    case SchemaType.REVIEWS:
    case SchemaType.RETURN_TRACKING:
    case SchemaType.ORDER_TRACKING:
    case SchemaType.YOTPO_LOYALTY_EXPIRATION_REMINDER:
    case SchemaType.YOTPO_LOYALTY_POINTS_REMINDER:
    case SchemaType.YOTPO_LOYALTY_REDEMPTION_REMINDER:
    case SchemaType.YOTPO_LOYALTY_REDEMPTION_CREATED:
    case SchemaType.YOTPO_LOYALTY_REFERRAL_COMPLETED:
    case SchemaType.YOTPO_LOYALTY_REFERRAL_SHARED:
    case SchemaType.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY:
    case SchemaType.YOTPO_LOYALTY_OPT_IN:
    case SchemaType.YOTPO_LOYALTY_TIER_EARNED:
    case SchemaType.YOTPO_LOYALTY_TIER_LOST:
    case SchemaType.YOTPO_LOYALTY_POINTS_EARNED:
    case SchemaType.YOTPO_REVIEW_CREATED:
    case SchemaType.LOYALTY_LION_ENROLLED:
    case SchemaType.LOYALTY_LION_REWARD_AVAILABLE:
    case SchemaType.CUSTOM_EVENT:
    case SchemaType.SMS_MARKETING_SIGNUP:
    case SchemaType.OMS_NEW_FULFILLMENT_GROUP:
    case SchemaType.OMS_ORDER_READY_FOR_PICKUP:
    case SchemaType.ADDRESS_VERIFICATION_FAILED:
    case SchemaType.TNPL_TRACKING:
    case SchemaType.EXTENDED_WARRANTY:
    case SchemaType.TRACKING_PAGE_FLOW:
    case SchemaType.RULES:
    case SchemaType.RULES_V3:
    case SchemaType.REFUND_RETURN_SUBMITTED:
    case SchemaType.EXCHANGE_PROCESSED_WITH_CREDIT:
    case SchemaType.MARKETING_AGENT:
    case SchemaType.RETURN_PROCESSING_FLOW:
      return UserPlusIcon;
    case SchemaType.MARKETING_DATE:
      return CalendarIcon;
    case SchemaType.MARKETING_PRICE_DROP:
      return TagIcon;
    case SchemaType.WORK_ORDER:
      return ToolIcon;
    default:
      assertNever(schemaType);
  }
}

function getStyledTriggerIconForSchemaType(schemaType: SchemaType | undefined) {
  const icon = getTriggerIconForSchemaType(schemaType);
  switch (schemaType) {
    case SchemaType.CHECKOUT_TREE:
    case SchemaType.RCO_CHECKOUT_PACKAGE:
    case SchemaType.POST_PURCHASE_FLOW:
    case SchemaType.MARKETING_CART_ABANDONMENT:
    case SchemaType.MARKETING_BROWSE_ABANDONMENT:
    case SchemaType.MARKETING_CHECKOUT_ABANDONMENT:
      return (
        <RedoFeaturedIcon
          color="black"
          Icon={icon}
          size="xs"
          type="high-contrast"
        />
      );
    default:
      return <RedoFeaturedIcon color="black" Icon={icon} size="xs" />;
  }
}

export function TriggerNode(data: {
  id: string;
  data: TriggerNodeData;
  selected?: boolean | undefined;
}): React.ReactNode {
  const featuredIcon = useMemo(() => {
    return getStyledTriggerIconForSchemaType(data.data.step.schemaType);
  }, [data.data.step.schemaType]);

  const isWorkOrder = data.data.step.schemaType === SchemaType.WORK_ORDER;

  const title = isWorkOrder
    ? (data.data.flowName ?? "Work order flow")
    : (getTriggerDisplayName(data.data.step.key) ?? "");

  if (isWorkOrder) {
    const source = data.data.triggerSource ?? WorkOrderTriggerSource.PRODUCT;
    const subtext =
      source === WorkOrderTriggerSource.BOM
        ? (() => {
            const bomCount = data.data.triggerBomIds?.length ?? 0;
            return bomCount > 0
              ? `${bomCount} BOM${bomCount === 1 ? "" : "s"} selected`
              : "No BOMs selected";
          })()
        : (() => {
            const countLeaves = (children: any[]): number => {
              return children.reduce((sum, child) => {
                if (child.nodeType === "leaf") return sum + 1;
                return sum + countLeaves(child.children || []);
              }, 0);
            };
            const count = countLeaves(
              data.data.triggerConditions?.children ?? [],
            );
            return count > 0
              ? `${count} product condition${count === 1 ? "" : "s"}`
              : "No conditions configured";
          })();

    const { wrapperStyle } = calculateSelectionWrapperStyles({
      selected: data.selected,
    });

    return (
      <>
        <div
          className={classNames(NodeCSS.nodeWrapper, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [NodeCSS.nodeWrapperSelected]: data.selected,
          })}
          style={{ ...wrapperStyle, display: "flex" }}
        >
          <Flex
            className={NodeCSS.analytics}
            dir="column"
            gap="sm"
            style={{ marginTop: 0 }}
          >
            <Text
              fontSize="sm"
              fontWeight="semibold"
              style={{ color: "white" }}
            >
              {title}
            </Text>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="semibold">
                Gather all BOM inputs
              </Text>
              <Text
                className={NodeCSS["secondary-metric"] as string}
                fontSize="xs"
              >
                {subtext}
              </Text>
            </Flex>
          </Flex>
        </div>
        <Handle position={Position.Bottom} type="source" />
      </>
    );
  }

  return (
    <>
      <BaseNode
        icon={featuredIcon}
        selected={data.selected}
        step={data.data.step}
        title={title}
      />

      {data.data.step.category &&
        flowCategoriesWithSkipConditions.includes(data.data.step.category) && (
          <Flex className={NodeCSS.analytics}>
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">Skip conditions</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {data.data.step.skipConditions?.conditions?.length ?? "-"}
              </Text>
            </Flex>
          </Flex>
        )}

      {data.data.step.schemaType === SchemaType.CUSTOM_EVENT && (
        <Flex className={NodeCSS.analytics}>
          <Flex dir="column" gap="xs" w="full">
            {"eventName" in data.data.step && (
              <Flex justify="space-between" w="full">
                <Text fontSize="xs">Event</Text>
                <Text fontSize="xs" fontWeight="semibold">
                  {(data.data.step.eventName as string | undefined) ?? "-"}
                </Text>
              </Flex>
            )}
            <Flex justify="space-between" w="full">
              <Text fontSize="xs">Property conditions</Text>
              <Text fontSize="xs" fontWeight="semibold">
                {("triggerSpecificFields" in data.data.step
                  ? (
                      data.data.step.triggerSpecificFields as
                        | { conditions?: unknown[] }
                        | null
                        | undefined
                    )?.conditions?.length
                  : undefined) ?? 0}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}

      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
